import { getDevice } from 'dss-device';
import { crawlerUserAgents } from '../utils/langRegionRedirect';

const deviceSupportMatrix = {
    chrome: 53,
    firefox: 49,
    msie: 11,
    safari: 9
};

const mobileDeviceBlacklist = [
    'silk'
];


/**
 * Mobile Device Check
 * @description Checks if on a mobile device or not
 * @returns {Boolean} whether device is mobile or not
 */
function isMobileDevice() {
    const device = getDevice();
    const isPhone = device.formFactor === 'phone';
    const isTablet = device.formFactor === 'tablet';

    return isPhone || isTablet;
}

/**
 * Mobile Device BlacklistCheck
 * @description Checks if the user is on a mobile device and whether it is black-listed or not
 * @returns {Boolean} whether device is mobile and blacklisted
 */
function isSupportedMobileDevice() {
    const device = getDevice();
    const browser = device.browser;

    return isMobileDevice() && !mobileDeviceBlacklist.includes(browser);
}


/**
 * Get Device Platform
 * @description Gets and returns device platform. ie: 'apple' or 'android'
 * @returns {String} the platform string
 */
function getDevicePlatform() {
    const device = getDevice();

    return device.platform || '';
}

/**
 * @description Checks userAgent to see if it is a supported crawler bot.
 * @param {string} userAgent the userAgent string
 * @return {Boolean} Whether the userAgent has been verified as a crawler bot.
 */
function isCrawlerBot(userAgent) {
    if (!userAgent) {
        return false;
    }

    return crawlerUserAgents.some(bot => userAgent.toLowerCase().includes(bot));
}

/**
 * @description Checks the IE browser version against the minimum we support and returns true or false
 * @returns {Boolean} Whether the browser is IE and is a supported version or not
 */
function isDeviceSupportedVersionOfSafari() {
    const device = getDevice();
    let isSupported = true;

    // check to see if the user is on osx and using safari
    if (device.browser === 'safari') {

        if (device.platform === 'macintosh') {
            const osxVersionSegments = device.platformVersion.split('.');

            // if the user is on El Capitan or lower, redirect to /unsupported (verison 11 is El Capitan)
            if ((osxVersionSegments.length >= 2) &&
                (osxVersionSegments[0] === '10') &&
                (parseInt(osxVersionSegments[1], 10) <= 11)
            ) {
                isSupported = false;
            }
        }
    }

    return isSupported;
}

/**
 * @description Checks the browser version against the minimum we support and returns true or false
 * @returns {Boolean} Whether the device is supported or not
 */
function isDeviceSupported() {
    const device = getDevice();
    const browser = device.browser;
    const version = parseInt(device.browserVersion, 10);
    const minVersion = deviceSupportMatrix[browser];

    //Should probably fail open
    let meetsMinimum = true;

    if (!isCrawlerBot(device.userAgent)) {
        if ((browser === 'silk') || !isDeviceSupportedVersionOfSafari()) {
            meetsMinimum = false;
        } else if (browser && version && minVersion) {
            meetsMinimum = version >= minVersion;
        }
    }

    return meetsMinimum;
}

/**
 * @description Checks the IE browser version against the minimum we support and returns true or false
 * @returns {Boolean} Whether the browser is IE and is a supported version or not
 */
function isDeviceSupportedVersionOfIE() {
    const device = getDevice();
    const browser = device.browser;

    return browser === 'msie' && isDeviceSupported();
}

/**
 * @description Checks the Edge browser version against the minimum we support and returns true or false
 * @returns {Boolean} Whether the browser is Edge and is a supported version or not
 */
function isDeviceSupportedVersionOfEdge() {
    return getDevice().browser === 'edge' && isDeviceSupported();
}

/**
 * @description Determines whether other not the current device has a retina display
 * @returns {Boolean} Whether the current device has a retina display
 */
function isRetinaDisplay() {
    //Wrapped in try/catch for server execution
    try {
        if (window.matchMedia) {
            const mediaQuery = '(-webkit-min-device-pixel-ratio: 2), ' +
                '(min-device-pixel-ratio: 2), (min-resolution: 192dpi)';

            return matchMedia(mediaQuery).matches;
        } else {
            return false;
        }
    } catch (e) {
        //Default to true for server generated images to minimize flicker
        return true;
    }
}

export {
    isCrawlerBot,
    isMobileDevice,
    isSupportedMobileDevice,
    getDevicePlatform,
    isDeviceSupported,
    isDeviceSupportedVersionOfEdge,
    isDeviceSupportedVersionOfIE,
    isDeviceSupportedVersionOfSafari,
    isRetinaDisplay
};
